import { useCallback, useMemo, useState } from 'react'
import { currentMonth, monthToRange, type MonthValue } from '../components/MonthFilter'

export type PeriodoModo = 'mes' | 'rango'
// `type` (no `interface`) a propósito: los alias de literal obtienen index
// signature implícita, por lo que RangoParcial es asignable a FiltrosRango /
// Record<string, ...> al pasarse a los hooks de datos y al menú de export.
export type RangoParcial = {
  desde?: string
  hasta?: string
}

interface Opciones {
  /** Estado inicial de "Todo el histórico" (ej. Participantes arranca en histórico). */
  sinFechaInicial?: boolean
}

/**
 * Estado del selector de periodo compartido por las secciones de reportes.
 * Unifica el patrón que estaba duplicado en cada página (mes + "Todo el
 * histórico") y agrega el modo "Rango personalizado".
 *
 * - `rango`: rango concreto {desde,hasta} para mostrar y para filtros
 *   client-side (Participantes). En modo rango, los extremos faltantes se
 *   completan con el mes vigente.
 * - `rangoEfectivo`: lo que consumen los hooks de datos. `{}` cuando "Todo el
 *   histórico" está activo; el rango personalizado (parcial, permite un solo
 *   extremo) en modo rango; o el mes en modo mes.
 */
export function usePeriodoReporte(opts?: Opciones) {
  const [mes, setMes] = useState<MonthValue>(currentMonth())
  const [sinFecha, setSinFecha] = useState(opts?.sinFechaInicial ?? false)
  const [modo, setModo] = useState<PeriodoModo>('mes')
  const [rangoPersonalizado, setRangoPersonalizado] = useState<RangoParcial>({})

  const rangoMes = useMemo(() => monthToRange(mes), [mes])

  // Al entrar a modo rango se siembra con el mes vigente para conservar el
  // periodo actual (mismo comportamiento que el filtro de multichannel).
  const cambiarModo = useCallback(
    (m: PeriodoModo) => {
      setModo(m)
      if (m === 'rango') {
        setRangoPersonalizado((prev) => (prev.desde || prev.hasta ? prev : { ...rangoMes }))
      }
    },
    [rangoMes],
  )

  const rango = useMemo<{ desde: string; hasta: string }>(() => {
    if (modo === 'rango') {
      return {
        desde: rangoPersonalizado.desde ?? rangoMes.desde,
        hasta: rangoPersonalizado.hasta ?? rangoMes.hasta,
      }
    }
    return rangoMes
  }, [modo, rangoPersonalizado, rangoMes])

  const rangoEfectivo = useMemo<RangoParcial>(() => {
    if (sinFecha) return {}
    if (modo === 'rango') {
      const r: RangoParcial = {}
      if (rangoPersonalizado.desde) r.desde = rangoPersonalizado.desde
      if (rangoPersonalizado.hasta) r.hasta = rangoPersonalizado.hasta
      return r
    }
    return rangoMes
  }, [sinFecha, modo, rangoPersonalizado, rangoMes])

  return {
    mes,
    setMes,
    sinFecha,
    setSinFecha,
    modo,
    setModo: cambiarModo,
    rangoPersonalizado,
    setRangoPersonalizado,
    rango,
    rangoEfectivo,
  }
}
